import { Dispatch, createContext, useContext, useReducer } from "react";

import { INewOrderQueryData } from "../models/newOrderQueryData";
import { ISavedProgress } from "../models/savedProgress";
import { IStep } from "../models/step";
import {
  IQueryDataAction,
  newOrderQueryDataReducer,
} from "./reducers/newOrderQueryDataReducer";
import {
  IProgressionAction,
  progressionReducer,
} from "./reducers/progressionReducer";
import {
  ISavedProgressAction,
  savedProgressReducer,
} from "./reducers/savedProgressReducer";
import { IStepsAction, stepsReducer } from "./reducers/stepsReducer";

//#region initial State Objects
export const initialSavedProgress: ISavedProgress = {};
export const initialProgressLevel: number = 1;
export const initialSteps: IStep[] = [];
export const initialQueryData: INewOrderQueryData = {};

//#endregion

interface IOrderContextObj {
  savedProgress: ISavedProgress;
  dispatchSavedProgress: Dispatch<ISavedProgressAction>;
  progressLevel: number;
  dispatchProgressLevel: Dispatch<IProgressionAction>;
  steps: IStep[];
  dispatchSteps: Dispatch<IStepsAction>;
  queryData: INewOrderQueryData;
  dispatchQueryData: Dispatch<IQueryDataAction>;
}

const OrderContext = createContext<IOrderContextObj>({
  savedProgress: initialSavedProgress,
  dispatchSavedProgress: () => {},
  progressLevel: initialProgressLevel,
  dispatchProgressLevel: () => {},
  steps: initialSteps,
  dispatchSteps: () => {},
  queryData: initialQueryData,
  dispatchQueryData: () => {},
});

export const useOrderContext = () => useContext(OrderContext);

interface OrderContextProviderProps {
  initialSteps: IStep[];
  initialProgressLevel: number;
}

export const OrderContextProvider = (
  props: React.PropsWithChildren<OrderContextProviderProps>,
) => {
  const { initialSteps, initialProgressLevel } = props;

  const [savedProgress, dispatchSavedProgress] = useReducer(
    savedProgressReducer,
    initialSavedProgress,
  );

  const [progressLevel, dispatchProgressLevel] = useReducer(
    progressionReducer,
    initialProgressLevel,
  );

  const [steps, dispatchSteps] = useReducer(stepsReducer, initialSteps);

  const [queryData, dispatchQueryData] = useReducer(
    newOrderQueryDataReducer,
    initialQueryData,
  );

  const value = {
    savedProgress,
    dispatchSavedProgress,
    progressLevel,
    dispatchProgressLevel,
    steps,
    dispatchSteps,
    queryData,
    dispatchQueryData,
  };

  return (
    <OrderContext.Provider value={value}>
      {props.children}
    </OrderContext.Provider>
  );
};
